// University of Montana: public college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UM's Common Data Set 2025-2026 (cds-pdf-2025-2026_pdf_template1.pdf,
//    the "Fiscal Year 2026" edition, newest on Institutional Research's
//    Common Data Set and CSRDE page). Its text layer is partly damaged, so
//    C1, C8, C13 to C17, C21/C22 and G1/G5 were read from the rendered
//    pages. C1 gives applicants and admits only by sex and leaves the
//    enrolled-by-sex rows blank: the counts here are the sums of its rows
//    (3,900 + 5,180 applied, 3,643 + 5,000 admitted, and enrolled 586 + 50 +
//    803 + 63 from the enrollees-by-status rows). Its residency table is
//    blank. C8A marks the SAT or ACT "not required for admission, but
//    consider if submitted" for Fall 2027 (the yes-or-no line above it is
//    marked No). C21/C22 report no early decision or early action plan.
//  - UM Undergraduate Admissions: Apply ("The Fall semester 2027
//    application is open now!", Early Action by December 1, Regular
//    Decision by May 1, 2027, test policy and application FAQs) and Dates
//    and Deadlines (priority date March 1). Departure: the CDS (C14 closing
//    date August 31, C22 no early action) describes the cycle it was filed
//    for; the admissions pages for fall 2027 win. Decision dates are not
//    published; UM says decisions typically come within 10 business days of
//    a complete application.
//  - Student Financial Services, Undergraduate Cost of Attendance, with its
//    2026-2027 In-State and Out-of-State pages (12 hours a semester, living
//    in a residence hall with a dining plan). The CDS G section marks
//    2026-2027 costs as not yet available ("June 2026"), so it is not used.
//    The in-state page's total ($34,086) is $1 more than the sum of its
//    items; the items' sum ($34,085) is used.
//  - College Scorecard API, school id 180489 (net price by family income,
//    in-state).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'University of Montana Common Data Set 2025-2026',
        url: 'https://www.umt.edu/institutional-research/data_reports/common_data_set.php',
        read: '2026-10-01',
    },
    apply: {
        label: 'University of Montana Undergraduate Admissions, Apply',
        url: 'https://www.umt.edu/admissions/apply-undergraduate.php',
        read: '2026-10-01',
    },
    coa: {
        label: 'University of Montana Student Financial Services, Undergraduate Cost of Attendance 2026-2027',
        url: 'https://www.umt.edu/student-financial-services/cost-of-attendance/undergraduate-coa/default.php',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?180489-The-University-of-Montana',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-montana',
    kbId: 'university_of_montana',
    name: 'University of Montana',
    shortName: 'UM',
    aliases: ['UM', 'UMontana', 'Montana'],
    location: 'Missoula, MT',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UM brand guide); decoration only.
    branding: {
        primary: '#70002E',
        secondary: '#999999',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_montana_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 9080,
        admitted: 8643,
        enrolled: 1502,
        source: 'cds',
        where: 'section C1 (totals are the sums of its rows by sex and by enrollment status)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UM\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'UM\'s Common Data Set reports no early decision or early action plan for the fall 2025 class, so it gives no early round acceptance rate. For fall 2027, UM lists a December 1 Early Action date (see the deadlines).',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'May 1' },
        rows: [
            { plan: 'Early Action', deadline: 'December 1', decision: null, binding: false },
            { plan: 'Regular Decision', deadline: 'May 1', decision: null, binding: false },
        ],
        note: 'After May 1, UM keeps accepting applications on a rolling basis as space allows, until one week before the term begins. The earlier you send a complete application, the higher your priority for processing and scholarships. Decisions typically come within 10 business days of a complete application; UM does not publish fixed decision dates. Montana residents apply through Apply Montana, which is free for them; out-of-state students use the Common App.',
        source: 'apply',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'ACT and SAT scores are not required for admission to UM, though students who have taken them are encouraged to send scores for course placement. You qualify for admission with any one of: a 2.5 GPA or higher, an ACT composite of 22 or higher, an SAT of 1120 or higher, or a class rank in the upper half. Selective programs with separate admission, such as the Davidson Honors College, may require SAT or ACT scores.',
        source: 'apply',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for admission but considered if submitted, for fall 2027.',
        cdsWhere: 'section C8A',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'apply',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [9750, 34894] },
            { item: 'Housing and meals', amounts: [14120, 14120] },
            { item: 'Books and supplies', amounts: [2046, 2046] },
            { item: 'Transportation', amounts: [2327, 3552] },
            { item: 'Miscellaneous', amounts: [3578, 3578] },
            { item: 'Health insurance premium', amounts: [2232, 2232] },
            { item: 'Loan fee allowance', amounts: [32, 32] },
        ],
        totals: [34085, 60454],
        note: 'For a full-time undergraduate taking 12 hours a semester, living in a residence hall with a dining plan, fall and spring. UM lists the in-state total as $34,086, $1 more than its items add up to. Students in the Western Undergraduate Exchange have a $39,334 budget.',
        source: 'coa',
        netPrice: {
            average: 21302,
            byIncome: [
                { income: '$0 to $30,000', amount: 19112 },
                { income: '$30,001 to $48,000', amount: 19857 },
                { income: '$48,001 to $75,000', amount: 21615 },
                { income: '$75,001 to $110,000', amount: 23224 },
                { income: 'Over $110,000', amount: 24484 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UM\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
